import React from "react";
import { captureException } from "../../../lib/error-tracking";

interface FallbackProps {
	error: Error;
	componentStack: string;
	resetError: () => void;
}

interface AppErrorBoundaryProps {
	fallback: (props: FallbackProps) => React.ReactNode;
	children: React.ReactNode;
}

interface AppErrorBoundaryState {
	error: Error | null;
	componentStack: string | null;
}

export class AppErrorBoundary extends React.Component<
	AppErrorBoundaryProps,
	AppErrorBoundaryState
> {
	state: AppErrorBoundaryState = { error: null, componentStack: null };

	static getDerivedStateFromError(error: Error) {
		return { error };
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		this.setState({ componentStack: info.componentStack ?? null });
		captureException(error, {
			componentStack: info.componentStack,
		});
	}

	handleReset = () => {
		this.setState({ error: null, componentStack: null });
	};

	render() {
		const { error, componentStack } = this.state;
		if (error) {
			return this.props.fallback({
				error,
				componentStack: componentStack ?? "",
				resetError: this.handleReset,
			});
		}
		return this.props.children;
	}
}
